/**
 * The fleet run sheet for one status (+ optional resellerId). TMMPDM-0020: carries
 * `unattributed[]` for the alert above the table.
 */
import { useCallback, useEffect, useState } from 'react';
import { adminApi } from '@/api/tmm/admin';
import type { PayoutStatus, PayoutStatementView, UnattributedRevenue } from '@/api/tmm/types';

export interface RunSheetData {
  payouts: PayoutStatementView[];
  unattributed: UnattributedRevenue[];
}

export interface UseRunSheet {
  data: RunSheetData | null;
  loading: boolean;
  error: boolean;
  /** The failure behind `error`, for `ErrorState`'s reference id. */
  failure: unknown;
  refetch: () => void;
}

export function useRunSheet(status: PayoutStatus, resellerId?: number): UseRunSheet {
  const [reloadToken, setReloadToken] = useState(0);
  const [state, setState] = useState<{
    key: string;
    data: RunSheetData | null;
    error: boolean;
    failure?: unknown;
  }>({
    key: '',
    data: null,
    error: false,
  });
  const key = `${status}:${resellerId ?? ''}:${reloadToken}`;

  useEffect(() => {
    let cancelled = false;
    adminApi
      .payoutRunSheet(status, resellerId)
      .then((sheet) => {
        if (cancelled) return;
        setState({
          key,
          data: { payouts: sheet.payouts, unattributed: sheet.unattributed },
          error: false,
        });
      })
      .catch((failure: unknown) => {
        if (!cancelled) setState({ key, data: null, error: true, failure });
      });
    return () => {
      cancelled = true;
    };
  }, [key, status, resellerId]);

  const refetch = useCallback(() => setReloadToken((n) => n + 1), []);
  const current = state.key === key;
  return {
    data: current ? state.data : null,
    loading: !current,
    error: current && state.error,
    failure: current ? state.failure : undefined,
    refetch,
  };
}
